import type { SluggedId } from "Models";
import type { Clubhouse_LeagueV2RosterMemberDto } from "../types/leagueRoster";
import type {
  Clubhouse_LeagueV2TeamsListDto,
  Clubhouse_LeagueV2TeamPlayerRowDto,
  Clubhouse_LeagueV2TeamSummaryDto,
} from "../types/leagueTeams";

/**
 * The cache edits behind the roster and Teams board's optimistic writes.
 *
 * They live here, apart from the mutations that dispatch them, because they
 * are the part with decisions in it — which row matches, what counts as the
 * same player, what to put back when a write fails — and because an Immer
 * draft is just an object, so each one can be checked against a plain array
 * without a store, a dispatch, or a round-trip.
 *
 * One rule runs through all of them: **ids are compared as strings.** A write
 * carries the ids the caller was given; a read hands back opaque slugs for
 * the same person or team. Comparing the two directly finds no match even
 * when there is one — which drew players onto a team twice before it was
 * caught.
 */

const sameId = (a: unknown, b: unknown) => String(a) === String(b);

// ── Roster ───────────────────────────────────────────────────────────────────

/** Add a row for a player the server has not confirmed yet. */
export function addOptimisticMember(
  draft: Clubhouse_LeagueV2RosterMemberDto[],
  member: Clubhouse_LeagueV2RosterMemberDto,
): void {
  if (!Array.isArray(draft)) return;
  draft.push(member);
}

/**
 * Swap the placeholder row for the server's own, once it lands. Matched by
 * the temporary id, which is the only thing the two have in common — the
 * server's row carries an id this client has never seen.
 */
export function replaceOptimisticMember(
  draft: Clubhouse_LeagueV2RosterMemberDto[],
  tempId: SluggedId,
  member: Clubhouse_LeagueV2RosterMemberDto,
): void {
  if (!Array.isArray(draft)) return;
  const i = draft.findIndex((m) => sameId(m.memberId, tempId));
  if (i === -1) return;
  draft[i] = member;
}

/**
 * Drop a member and hand them back, so a failed delete can put the same row
 * in again. Returned as a plain copy rather than the draft element itself:
 * that element is an Immer proxy, revoked the moment the recipe returns, and
 * reusing it in a later patch throws.
 */
export function removeMemberById(
  draft: Clubhouse_LeagueV2RosterMemberDto[],
  memberId: SluggedId,
): Clubhouse_LeagueV2RosterMemberDto | undefined {
  if (!Array.isArray(draft)) return undefined;
  const i = draft.findIndex((m) => sameId(m.memberId, memberId));
  if (i === -1) return undefined;
  const removed = { ...draft[i]! };
  draft.splice(i, 1);
  return removed;
}

/** The fields a roster edit can change in the cache. */
export type MemberEdit = Pick<
  Clubhouse_LeagueV2RosterMemberDto,
  | "seedHandicap"
  | "currentHandicap"
  | "handicapStatus"
  | "isActive"
  | "isSubstituteOnly"
  | "teeType"
>;

/**
 * Apply an edit to one member, and hand back what those fields held before.
 *
 * The caller keeps that and passes it straight back here if the write fails,
 * which is a second edit rather than an undo. Immer's inverse for a field
 * assignment is a `replace` at an array index, and this mutation
 * invalidates — so a concurrent add or remove can refetch and reorder the
 * roster before the failure lands, and replaying the inverse would write one
 * player's old handicap onto whoever now occupies that index.
 *
 * Only the keys actually present are touched, so "no change" and "set it to
 * undefined" stay distinguishable in both directions.
 */
export function applyMemberEdit(
  draft: Clubhouse_LeagueV2RosterMemberDto[],
  memberId: SluggedId,
  edit: MemberEdit,
): MemberEdit | undefined {
  if (!Array.isArray(draft)) return undefined;
  const member = draft.find((m) => sameId(m.memberId, memberId));
  if (!member) return undefined;
  const previous: MemberEdit = {};
  for (const key of Object.keys(edit) as (keyof MemberEdit)[]) {
    // Recorded even when undefined: the restore has to be able to put a
    // field back to "not set", not just to another value.
    (previous as Record<string, unknown>)[key] = member[key];
    (member as Record<string, unknown>)[key] = edit[key];
  }
  return previous;
}

/**
 * The same, for the delete that identifies a player by their roster user id
 * rather than their membership id — the two endpoints take different keys
 * for the same act.
 */
export function removeMemberByUserId(
  draft: Clubhouse_LeagueV2RosterMemberDto[],
  userId: SluggedId,
): Clubhouse_LeagueV2RosterMemberDto | undefined {
  if (!Array.isArray(draft)) return undefined;
  const i = draft.findIndex((m) => sameId(m.userId, userId));
  if (i === -1) return undefined;
  const removed = { ...draft[i]! };
  draft.splice(i, 1);
  return removed;
}

/**
 * Put a removed member back after a failed delete.
 *
 * By id, against whatever the list looks like when the failure lands — not by
 * replaying the splice that took them out. A concurrent delete on the same
 * roster will have changed the array's shape by then, and a positional undo
 * would land at the wrong index or clobber a different row.
 */
export function restoreMember(
  draft: Clubhouse_LeagueV2RosterMemberDto[],
  member: Clubhouse_LeagueV2RosterMemberDto,
): void {
  if (!Array.isArray(draft)) return;
  // Either key identifies them: the two delete endpoints take different
  // ones, and whichever is present has to stop a double restore.
  const alreadyBack = draft.some(
    (m) =>
      (member.memberId != null && sameId(m.memberId, member.memberId)) ||
      (member.userId != null && sameId(m.userId, member.userId)),
  );
  if (alreadyBack) return;
  draft.push(member);
}

// ── Teams ────────────────────────────────────────────────────────────────────

const findTeam = (
  draft: Clubhouse_LeagueV2TeamsListDto,
  teamId: unknown,
): Clubhouse_LeagueV2TeamSummaryDto | undefined =>
  draft.teams?.find((t) => sameId(t.teamId, teamId));

/** Add a team the server has not confirmed yet. */
export function addOptimisticTeam(
  draft: Clubhouse_LeagueV2TeamsListDto,
  team: Clubhouse_LeagueV2TeamSummaryDto,
): void {
  draft.teams ??= [];
  draft.teams.push(team);
}

/**
 * Draw players onto a team before the write lands.
 *
 * Skips anyone already there. The ids being assigned come from the write
 * side and the ids on the row came from a read, so this is the comparison
 * that has to go through `String` — matching on the raw values missed
 * players who were already on the team and drew them a second time.
 */
export function assignPlayersToTeam(
  draft: Clubhouse_LeagueV2TeamsListDto,
  teamId: unknown,
  userIds: readonly (string | number)[],
): (string | number)[] {
  const team = findTeam(draft, teamId);
  if (!team) return [];
  team.players ??= [];
  const present = new Set(team.players.map((p) => String(p.userId)));
  const inserted: (string | number)[] = [];
  for (const userId of userIds) {
    if (present.has(String(userId))) continue;
    // A placeholder id, standing in until the refetch replaces this row with
    // the server's — which is the only thing that knows this player's slug.
    team.players.push({ userId: userId as SluggedId });
    present.add(String(userId));
    inserted.push(userId);
  }
  // Reported back so a failure can take out exactly what this call put in,
  // and not a player who was already on the team before it ran.
  return inserted;
}

/**
 * Take named players back off a team.
 *
 * Both ways an assignment can come undone end here. The server can refuse
 * one on an otherwise-successful response — a 200 does not mean every id
 * landed, and a player it rejects (team full, already on another team) comes
 * back in `rejected`. Or the whole write can fail, and the players this call
 * drew on have to come off again.
 *
 * By id, against whatever the cache holds when it runs — never by replaying
 * the insert's inverse. Immer's inverse patch for an append removes the
 * index captured at dispatch, and these mutations invalidate, so a
 * concurrent assignment can have refetched and reordered the array by then:
 * the undo would take out whichever player now sits in that slot, deleting a
 * teammate whose own write had already succeeded.
 */
export function dropPlayersFromTeam(
  draft: Clubhouse_LeagueV2TeamsListDto,
  teamId: unknown,
  userIds: readonly (string | number)[],
): void {
  const team = findTeam(draft, teamId);
  if (!team?.players) return;
  const dropping = new Set(userIds.map(String));
  team.players = team.players.filter((p) => !dropping.has(String(p.userId)));
}

/** Take back a team the create failed for, by its placeholder id. */
export function removeTeamById(
  draft: Clubhouse_LeagueV2TeamsListDto,
  teamId: unknown,
): void {
  if (!draft.teams) return;
  draft.teams = draft.teams.filter((t) => !sameId(t.teamId, teamId));
}

/** Drop a player from a team and hand them back, for the same reason as above. */
export function removePlayerFromTeam(
  draft: Clubhouse_LeagueV2TeamsListDto,
  teamId: unknown,
  userId: unknown,
): Clubhouse_LeagueV2TeamPlayerRowDto | undefined {
  const team = findTeam(draft, teamId);
  if (!team?.players) return undefined;
  const i = team.players.findIndex((p) => sameId(p.userId, userId));
  if (i === -1) return undefined;
  const removed = { ...team.players[i]! };
  team.players.splice(i, 1);
  return removed;
}

/** Put a player back on their team after a failed removal. */
export function restorePlayerOnTeam(
  draft: Clubhouse_LeagueV2TeamsListDto,
  teamId: unknown,
  player: Clubhouse_LeagueV2TeamPlayerRowDto,
): void {
  const team = findTeam(draft, teamId);
  if (!team) return;
  team.players ??= [];
  if (team.players.some((p) => sameId(p.userId, player.userId))) return;
  team.players.push(player);
}
